import { useState, useEffect } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../../api/staff'
import type { AuthSession } from '../../../../types/plans'
import { toDateInputValue, defaultOfferDeadline } from '../constructorTypes'
import type { ConstructorPlan } from '../constructorTypes'

export function PublicOfferPanel({
  plan,
  bootstrap,
  session,
  onReload,
}: {
  plan: ConstructorPlan
  bootstrap: TenantBootstrapModel
  session: AuthSession
  onReload: () => void
}) {
  const [deadline, setDeadline] = useState(toDateInputValue(plan.offer_deadline) || defaultOfferDeadline())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setDeadline(toDateInputValue(plan.offer_deadline) || defaultOfferDeadline())
    setError('')
  }, [plan.offer_deadline])

  async function handleSave(nextDeadline: string | null = deadline) {
    setSaving(true)
    setError('')
    try {
      await staffApi.updateConstructorPlan(bootstrap, session, plan.id, { offer_deadline: nextDeadline })
      onReload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить срок оффера')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-800">Таймер оффера</h3>
      <p className="mt-1 text-[11px] text-slate-600">
        На публичной странице покажем "Цена зафиксирована до …". Если очистить, блок не рендерится.
      </p>
      <label className="mt-3 flex flex-col gap-1 text-xs">
        <span className="font-medium text-slate-600">Срок действия предложения</span>
        <input
          type="date"
          className="rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-amber-400 focus:outline-none"
          value={deadline}
          onChange={(event) => setDeadline(event.target.value)}
          disabled={saving}
        />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
          onClick={() => void handleSave(deadline || null)}
          disabled={saving}
        >
          {saving ? 'Сохранение...' : 'Сохранить срок'}
        </button>
        <button
          type="button"
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          onClick={() => {
            const next = defaultOfferDeadline()
            setDeadline(next)
            void handleSave(next)
          }}
          disabled={saving}
        >
          +7 дней
        </button>
        <button
          type="button"
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          onClick={() => {
            setDeadline('')
            void handleSave(null)
          }}
          disabled={saving}
        >
          Убрать таймер
        </button>
      </div>
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
    </div>
  )
}
